import { memo as _$memo } from "@opentui/solid";
import { createComponent as _$createComponent } from "@opentui/solid";
import { mergeProps as _$mergeProps } from "@opentui/solid";
import { createSlotRegistry } from "@opentui/core";
import { children, createMemo, createSignal, ErrorBoundary, For, onCleanup, splitProps } from "solid-js/dist/solid.js";
const EMPTY_ENTRY_IDS = [];
export function createSolidSlotRegistry(renderer, context, options = {}) {
  // Solid slots intentionally use one registry key per renderer instance.
  // Use createSlotRegistry from @opentui/core with a custom key for independent registries.
  return createSlotRegistry(renderer, "solid:slot-registry", context, options);
}
export function createSlot(registry, options = {}) {
  return function BoundSlot(props) {
    return _$createComponent(Slot, _$mergeProps(props, {
      registry: registry,
      get pluginFailurePlaceholder() {
        return options.pluginFailurePlaceholder;
      }
    }));
  };
}
export function Slot(props) {
  const [local, slotProps] = splitProps(props, ["registry", "name", "mode", "children", "pluginFailurePlaceholder"]);
  const registry = () => local.registry;
  const pluginFailurePlaceholder = () => local.pluginFailurePlaceholder;
  const [version, setVersion] = createSignal(0);
  let queued = false;
  let disposed = false;
  const unsubscribe = registry().subscribe(() => {
    if (queued) return;
    queued = true;
    setVersion(current => current + 1);
    queueMicrotask(() => {
      queued = false;
      if (disposed) return;
    });
  });
  onCleanup(() => {
    disposed = true;
    unsubscribe();
  });
  const entries = createMemo((previousEntries = []) => {
    version();
    const resolvedEntries = registry().resolveEntries(local.name);
    if (resolvedEntries.length === 0) {
      if (previousEntries.length === 0) return previousEntries;
      return [];
    }
    const previousById = new Map(previousEntries.map(entry => [entry.id, entry]));
    const nextEntries = resolvedEntries.map(entry => {
      const previousEntry = previousById.get(entry.id);
      if (previousEntry && previousEntry.renderer === entry.renderer) {
        return previousEntry;
      }
      return entry;
    });
    const unchanged = nextEntries.length === previousEntries.length && nextEntries.every((entry, index) => entry === previousEntries[index]);
    if (unchanged) return previousEntries;
    return nextEntries;
  });
  const entryIds = createMemo(() => entries().map(entry => entry.id));
  const entriesById = createMemo(() => new Map(entries().map(entry => [entry.id, entry])));
  const slotName = () => String(local.name);
  const renderFallback = () => {
    const value = children(() => local.children)();
    return value ?? null;
  };
  const resolveFallback = fallbackValue => fallbackValue?.() ?? null;
  const renderPluginFailurePlaceholder = (failure, fallbackValue) => {
    if (!pluginFailurePlaceholder()) {
      return resolveFallback(fallbackValue);
    }
    try {
      return pluginFailurePlaceholder()(failure);
    } catch (error) {
      registry().reportPluginError({
        pluginId: failure.pluginId,
        slot: failure.slot ?? slotName(),
        phase: "error_placeholder",
        source: "solid",
        error
      });
      return resolveFallback(fallbackValue);
    }
  };
  const renderEntry = (entry, fallbackOnError) => {
    let initialRender;
    try {
      initialRender = entry.renderer(registry().context, slotProps);
    } catch (error) {
      const failure = registry().reportPluginError({
        pluginId: entry.id,
        slot: slotName(),
        phase: "render",
        source: "solid",
        error
      });
      return renderPluginFailurePlaceholder(failure, fallbackOnError);
    }
    const resolvedInitialRender = children(() => initialRender);
    const hasInitialOutput = resolvedInitialRender.toArray().some(node => node !== null && node !== undefined && node !== false);
    if (!hasInitialOutput) {
      return resolveFallback(fallbackOnError);
    }
    return _$createComponent(ErrorBoundary, {
      fallback: error => {
        const failure = registry().reportPluginError({
          pluginId: entry.id,
          slot: slotName(),
          phase: "render",
          source: "solid",
          error
        });
        return renderPluginFailurePlaceholder(failure, fallbackOnError);
      },
      get children() {
        return resolvedInitialRender();
      }
    });
  };
  const AppendEntry = appendProps => {
    const entry = createMemo(() => entriesById().get(appendProps.entryId));
    return _$memo(() => {
      const resolvedEntry = entry();
      if (!resolvedEntry) {
        return null;
      }
      return renderEntry(resolvedEntry);
    });
  };
  const appendEntryIds = createMemo(() => {
    const mode = local.mode ?? "append";
    if (mode !== "append") {
      return EMPTY_ENTRY_IDS;
    }
    return entryIds();
  });
  const appendView = [renderFallback, _$createComponent(For, {
    get each() {
      return appendEntryIds();
    },
    children: entryId => _$createComponent(AppendEntry, {
      entryId: entryId
    })
  })];
  return _$memo(() => {
    const resolvedEntries = entries();
    const mode = local.mode ?? "append";
    if (resolvedEntries.length === 0) {
      return renderFallback();
    }
    if (mode === "single_winner") {
      const winner = resolvedEntries[0];
      if (!winner) {
        return renderFallback();
      }
      return renderEntry(winner, renderFallback);
    }
    if (mode === "replace") {
      const renderedEntries = resolvedEntries.map(entry => renderEntry(entry));
      const hasPluginOutput = renderedEntries.some(entry => entry !== null && entry !== undefined && entry !== false);
      if (!hasPluginOutput) {
        return renderFallback();
      }
      return renderedEntries;
    }
    return appendView;
  });
}